import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import type { BreadcrumbMatch } from 'use-react-router-breadcrumbs';
import { GET_SINGLE_ASSET_NAME_ONLY } from '@api/queries/assets';
import { GET_SINGLE_COMPONENT_NAME_ONLY } from '@api/queries/components';
import { GET_SINGLE_ZONE_NAME_ONLY } from '@api/queries/projectZones';
import { GET_SINGLE_PROJECT_NAME_ONLY } from '@api/queries/projects';
import { GET_SINGLE_REQUIREMENT_NAME_ONLY } from '@api/queries/requirements';
import { GET_PROFILE_NAME_ONLY } from '@api/queries/standardProfiles';
import { GET_SINGLE_STANDARD_NAME_ONLY } from '@api/queries/standards';
import { GET_SINGLE_TASK } from '@api/queries/tasks';
import { truncateString } from '@utils/truncate-string';
import { CRA_TOPICS, TopicKey } from '@pages/project/cra/models/cra.types';

interface MatchProps {
    match: BreadcrumbMatch;
}

export function useGetStandardNameFromId(id: string | undefined): string {
    const { data } = useQuery(GET_SINGLE_STANDARD_NAME_ONLY, {
        variables: { sid: id ?? '' },
    });
    return data?.standards.edges[0]?.node.name ?? '...';
}

function useGetStandardBreadcrumb({ match }: MatchProps): string {
    return useGetStandardNameFromId(match.params.sid);
}

export function useProjectNameFromId(id: string | undefined): string {
    const { data } = useQuery(GET_SINGLE_PROJECT_NAME_ONLY, {
        skip: !id,
        variables: { pid: id ?? '' },
    });
    return data?.projects?.edges[0]?.node.name ?? '...';
}

function useProjectBreadcrumb({ match }: MatchProps): string {
    return useProjectNameFromId(match.params.pid);
}

export function useGetRequirementNameFromId(id: string | undefined): string {
    const { error, data } = useQuery(GET_SINGLE_REQUIREMENT_NAME_ONLY, {
        variables: { rid: id ?? '' },
    });

    if (error ?? !data) return '...';
    return data.requirements.edges[0]?.node.name ?? '...';
}

function useGetRequirementBreadcrumb({ match }: MatchProps): string {
    return useGetRequirementNameFromId(match.params.rid);
}

function useGetStandardProfileBreadcrumb({ match }: MatchProps): string {
    const { data } = useQuery(GET_PROFILE_NAME_ONLY, {
        variables: { profileId: match.params.profileId ?? '' },
        skip: !match.params.profileId,
    });

    return data?.standardProfiles.edges[0]?.node.name ?? '...';
}

function useGetStandardProfileBreadcrumbFromSpid({
    match,
}: MatchProps): string {
    const { data } = useQuery(GET_PROFILE_NAME_ONLY, {
        variables: { profileId: match.params.spid ?? '' },
        skip: !match.params.spid,
    });

    return data?.standardProfiles.edges[0]?.node.name ?? '...';
}

export function useGetProjectZoneNameFromId(id: string | undefined): string {
    const { pid } = useParams();
    const { error, data } = useQuery(GET_SINGLE_ZONE_NAME_ONLY, {
        variables: { pzid: id ?? '', pid: pid ?? '' },
    });
    if (error ?? !data) return '...';
    return data.projectZones.edges[0]?.node.name || '...';
}

function useGetProjectZoneBreadcrumb({ match }: MatchProps): string {
    return useGetProjectZoneNameFromId(match.params.nodeId);
}

function useGetProjectZoneBreadcrumbFromPzid({ match }: MatchProps): string {
    return useGetProjectZoneNameFromId(match.params.pzid);
}

function useCraTopicBreadcrumb({ match }: MatchProps): string {
    return CRA_TOPICS[match.params.topic as TopicKey]?.title ?? 'EU CRA Topic';
}

export function useGetAssetNameFromId(id: string | undefined): string {
    const { error, data } = useQuery(GET_SINGLE_ASSET_NAME_ONLY, {
        variables: { assetId: id ?? '' },
    });
    if (error ?? !data) return '...';
    return data.assets.edges[0]?.node.name || '...';
}

function useGetAssetBreadcrumb({ match }: MatchProps): string {
    return `${useGetAssetNameFromId(match.params.assetId)} items`;
}

export function useGetComponentNameFromId(id: string | undefined): string {
    const { error, data } = useQuery(GET_SINGLE_COMPONENT_NAME_ONLY, {
        variables: { componentId: id ?? '' },
    });
    if (error ?? !data) return '...';
    return data.components.edges[0]?.node.name || '...';
}

function useGetComponentBreadcrumb({ match }: MatchProps): string {
    return useGetComponentNameFromId(match.params.componentId);
}

function useGetTaskFromId(id: string | undefined): { title: string } | null {
    const { error, data } = useQuery(GET_SINGLE_TASK, {
        variables: { id: id ?? '' },
    });
    if (error ?? !data?.tasks) return null;
    return data.tasks.edges[0].node;
}

function useTaskBreadcrumb({ match }: MatchProps): string {
    return truncateString(
        useGetTaskFromId(match.params.tid)?.title ?? '...',
        50,
    );
}

export default [
    {
        path: '/projects/create',
        breadcrumb: 'Create',
    },
    {
        path: '/projects/:pid',
        breadcrumb: useProjectBreadcrumb,
    },
    {
        path: '/projects/:pid/guidance/:tid',
        breadcrumb: useTaskBreadcrumb,
    },
    {
        path: '/projects/:pid/guidance',
        breadcrumb: 'Project guidance',
    },
    {
        path: '/projects/:pid/security-zones/:nodeId',
        breadcrumb: useGetProjectZoneBreadcrumb,
    },
    {
        path: '/projects/:pid/security-zones/:nodeId/standard-profile/:profileId',
        breadcrumb: useGetStandardProfileBreadcrumb,
    },
    {
        path: '/projects/:pid/security-zones/:nodeId/standard-profile/:profileId/:rid',
        breadcrumb: useGetRequirementBreadcrumb,
    },
    {
        path: '/projects/:pid/assets/:assetId',
        breadcrumb: useGetAssetBreadcrumb,
    },
    {
        path: '/projects/:pid/security-zones-assets',
        breadcrumb: 'Security zones & assets',
    },
    {
        path: '/projects/:pid/security-zones-assets/:zid',
        breadcrumb: null,
    },
    {
        path: '/projects/:pid/security-zones-assets/requirements',
        breadcrumb: 'Requirements',
    },
    {
        path: '/projects/:pid/security-zones-assets/requirements/:pzid',
        breadcrumb: useGetProjectZoneBreadcrumbFromPzid,
    },
    {
        path: '/projects/:pid/security-zones-assets/requirements/:pzid/:spid',
        breadcrumb: useGetStandardProfileBreadcrumbFromSpid,
    },
    {
        path: '/projects/:pid/components',
        breadcrumb: 'Notification components',
    },
    {
        path: '/standards/:sid',
        breadcrumb: useGetStandardBreadcrumb,
    },
    {
        path: '/standards/:sid/requirements',
        breadcrumb: 'Requirements',
    },
    {
        path: '/standards/:sid/:spid',
        breadcrumb: useGetStandardProfileBreadcrumbFromSpid,
    },
    {
        path: '/standards/:sid/:spid/requirements',
        breadcrumb: 'Requirements',
    },
    {
        path: '/standards/:sid/requirements/:rid',
        breadcrumb: useGetRequirementBreadcrumb,
    },
    {
        path: '/projects/:pid/systems-and-assets',
        breadcrumb: 'Systems & assets',
    },
    {
        path: '/projects/:pid/systems-and-assets/:sid',
        breadcrumb: null,
    },
    {
        path: '/projects/:pid/cra',
        breadcrumb: 'EU CRA Module',
    },
    {
        path: '/projects/:pid/cra/:topic',
        breadcrumb: useCraTopicBreadcrumb,
    },
];
